import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { putPassword } from '@/auth/api';
import { FormTextField } from '@/components/form/form-text-field';
import { BevelWindow } from '@/components/prototype/bevel-window';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { applyApiErrors } from '@/pages/auth/form-errors';
import { newPasswordSchema } from '@/pages/auth/password-rules';

const schema = z
  .object({
    current_password: z.string().min(1),
    password: newPasswordSchema,
    password_confirmation: z.string().min(1),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type PasswordValues = z.infer<typeof schema>;

export function PasswordSection() {
  const { t } = useTranslation('auth');

  const form = useForm<PasswordValues>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: '', password: '', password_confirmation: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await putPassword(values);
      form.reset();
      toast.success(t('settings.passwordSaved'));
    } catch (error) {
      applyApiErrors(error, form.setError, (message) => toast.error(message));
    }
  });

  return (
    <BevelWindow title={t('settings.password')}>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormTextField
            control={form.control}
            name="current_password"
            label={t('fields.currentPassword')}
            type="password"
            autoComplete="current-password"
          />
          <FormTextField
            control={form.control}
            name="password"
            label={t('fields.password')}
            type="password"
            autoComplete="new-password"
          />
          <FormTextField
            control={form.control}
            name="password_confirmation"
            label={t('fields.passwordConfirm')}
            type="password"
            autoComplete="new-password"
          />
          <div>
            <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
              {t('settings.password')}
            </Button>
          </div>
        </form>
      </Form>
    </BevelWindow>
  );
}
